/**
 * @file components/home/CaseStoryCard.tsx
 * @description 公開首頁「客戶案例」區塊的單張大卡。
 *
 * 結構：上方真實照片（21:10，CASE-XXX 與方案 chip 已烘焙在圖內）+ 下方文字（客戶 / 地點 / 引言 / metric）。
 * Hover 行為與 SchemeCard 一致：微浮起 + 邊框加深 + 細微陰影。
 */

import type { CaseStory } from '@/components/home/home-data';

interface CaseStoryCardProps {
  story: CaseStory;
}

export function CaseStoryCard({ story }: CaseStoryCardProps) {
  return (
    <article
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-base)',
        border: '1px solid var(--stone-200)',
        borderRadius: 12,
        overflow: 'hidden',
        transition: 'all 200ms ease-out',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.borderColor = 'var(--stone-300)';
        e.currentTarget.style.boxShadow = '0 12px 28px -16px rgba(0,0,0,0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = 'var(--stone-200)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* 案例照片：原圖長寬比約 21:10，CASE 編號與方案標籤已疊在圖內 */}
      <div
        style={{
          position: 'relative',
          aspectRatio: '21 / 10',
          overflow: 'hidden',
          background: 'var(--stone-100)',
        }}
      >
        <img
          src={story.imageSrc}
          alt={story.imageAlt}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>

      {/* 文字區 */}
      <div style={{ padding: '24px 24px 28px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* 方案 chip */}
        <span
          style={{
            alignSelf: 'flex-start',
            padding: '3px 10px',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '0.08em',
            color: story.schemeColor,
            background: story.schemeBg,
            borderRadius: 999,
            marginBottom: 14,
          }}
        >
          {story.scheme}
        </span>

        {/* 客戶名 */}
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 20,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'var(--stone-900)',
            margin: '0 0 4px',
          }}
        >
          {story.client}
        </h3>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '0.08em',
            color: 'var(--stone-500)',
            marginBottom: 14,
          }}
        >
          {story.location}
        </div>

        {/* 引言（中文全形引號） */}
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.65,
            color: 'var(--stone-700)',
            margin: '0 0 24px',
            flex: 1,
          }}
        >
          「{story.quote}」
        </p>

        {/* metric 條 */}
        <div
          style={{
            paddingTop: 18,
            borderTop: '1px solid var(--stone-150)',
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 600,
              color: 'var(--stone-900)',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.01em',
            }}
          >
            {story.metric}
          </span>
          <span style={{ fontSize: 12, color: 'var(--stone-500)' }}>{story.metricLabel}</span>
        </div>
      </div>
    </article>
  );
}
